/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { FetchResult } from '@apollo/client'
import clsx from 'clsx'
import debounce from 'lodash/debounce'
import first from 'lodash/first'
import isEmpty from 'lodash/isEmpty'
import isFunction from 'lodash/isFunction'
import mapValues from 'lodash/mapValues'
import React, { FC, MutableRefObject, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'

import { ampli } from '@Analytics/ampli'
import { identify } from '@Analytics/identify'
import HomeIcon from '@Assets/SVG/AddressHome'
import SvgAngleLeft from '@Assets/SVG/AngleLeft'
import { Button } from '@CoreComponents/Buttons/Button'
import { useSavedCheckoutLocalData } from '@Domains/Checkout/Hooks/useSavedCheckoutLocalData'
import { PhonePrefixItem } from '@Domains/Checkout/Steps/Address/Components/PhonePrefixItem/PhonePrefixItem'
import { CheckoutFlowContext } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/CheckoutFlowContext'
import { CheckoutFlowStep, CheckoutFlowSteps } from '@Domains/CheckoutV2/Features/CheckoutSteps/Contexts/typings/CheckoutFlowTypes'
import { useCheckoutFieldsValidations } from '@Domains/CheckoutV2/Features/CheckoutSteps/Hooks/UseCheckoutFlowStepValidation'
import { useCheckoutSubscribeToMail } from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutAddressStep/Hooks/UseCheckoutSubscribeToMail'
import {
  PhonePrefixCountryCodeMap,
  PhonePrefixLocaleCodeMap,
} from '@Domains/CheckoutV2/Features/CheckoutSteps/Steps/CheckoutAddressStep/Utils/PhonePrefixLocaleCodeMap'
import { ClubTerms } from '@Domains/Club/Components/ClubTerms'
import { SetAddressesInCartMutation, useSetAddressesInCartMutation } from '@Generated/graphql'
import { useSplitTest } from '@Globals/SplitTests/Hooks'
import { useTranslation } from '@Globals/Translations'
import { CheckoutCartV2Address } from '@Pages/onestepcheckout'
import { addZipCodeDash } from '@Shared/Components/Forms/Validations/ZipCodeValidation'
import { Checkbox } from '@Shared/Components/Inputs/Checkbox/Checkbox'
import { SelectInput } from '@Shared/Components/Inputs/Select/SelectInput'
import { SelectInputCustom } from '@Shared/Components/Inputs/Select/SelectInputCustom'
import { SelectObject } from '@Shared/Components/Inputs/Select/SelectObject'
import { TextInput, TextInputActionProps, useTextInputActionRef } from '@Shared/Components/Inputs/TextInput/TextInput'
import { CollapsableSection } from '@Shared/Components/Sections/CollapsableSection/CollapsableSection'

import styles from './CheckoutAddressStep.module.scss'

const BillingAddressRefs = () => {
  const email = useTextInputActionRef()
  const firstName = useTextInputActionRef()
  const lastName = useTextInputActionRef()
  const streetName = useTextInputActionRef()
  const additionalStreetInfo = useTextInputActionRef()
  const city = useTextInputActionRef()
  const postalCode = useTextInputActionRef()
  const country = useTextInputActionRef()
  const state = useTextInputActionRef()
  const region = useTextInputActionRef()
  const phonePrefix = useTextInputActionRef()
  const phone = useTextInputActionRef()
  return {
    city,
    email,
    firstName,
    lastName,
    streetName,
    additionalStreetInfo,
    postalCode,
    country,
    state,
    region,
    phone,
    phonePrefix,

    validate() {
      return Boolean(
        (city.current?.validate() ?? true)
        && (email.current?.validate() ?? true)
        && (firstName.current?.validate() ?? true)
        && (lastName.current?.validate() ?? true)
        && (streetName.current?.validate() ?? true)
        && (additionalStreetInfo.current?.validate() ?? true)
        && (postalCode.current?.validate() ?? true)
        && (country.current?.validate() ?? true)
        && (state.current?.validate() ?? true)
        && (region.current?.validate() ?? true)
        && (phone.current?.validate() ?? true)
        && (phonePrefix.current?.validate() ?? true),
      )
    },
  }
}
const ShippingAddressRefs = () => {
  const firstName = useTextInputActionRef()
  const lastName = useTextInputActionRef()
  const company = useTextInputActionRef()
  const streetName = useTextInputActionRef()
  const additionalStreetInfo = useTextInputActionRef()
  const city = useTextInputActionRef()
  const postalCode = useTextInputActionRef()
  const country = useTextInputActionRef()
  const state = useTextInputActionRef()
  const region = useTextInputActionRef()
  return {
    firstName,
    lastName,
    company,
    streetName,
    additionalStreetInfo,
    postalCode,
    country,
    state,
    region,
    city,

    validate() {
      return Boolean(
        (firstName.current?.validate() ?? true)
        && (lastName.current?.validate() ?? true)
        && (company.current?.validate() ?? true)
        && (streetName.current?.validate() ?? true)
        && (additionalStreetInfo.current?.validate() ?? true)
        && (city.current?.validate() ?? true)
        && (postalCode.current?.validate() ?? true)
        && (country.current?.validate() ?? true)
        && (state.current?.validate() ?? true)
        && (region.current?.validate() ?? true),
      )
    },
  }
}
const useActionRefs = () => {
  const billingRefs = BillingAddressRefs()
  const shippingRefs = ShippingAddressRefs()
  return {
    billing: billingRefs,
    shipping: shippingRefs,

    validate() {
      return billingRefs.validate() && shippingRefs.validate()
    },
  }
}

export const replaceObjectWhere = (
  obj,
  replaceValuesEq: Array<string | null | undefined>,
  to: string | number | null | undefined,
) => mapValues(obj, v => (replaceValuesEq?.includes(v) ? to : v))

const analyticsIDRecord: Record<keyof CheckoutCartV2Address, string> = {
  streetName: 'cof_address',
  additionalStreetInfo: 'cof_address2',
  city: 'cof_city',
  company: 'cof_company',
  country: 'cof_country',
  email: 'cof_email',
  firstName: 'cof_first_name',
  lastName: 'cof_last_name',
  phonePrefix: 'cof_phone',
  phone: 'cof_phone',
  state: 'cof_country',
  region: 'cof_country',
  postalCode: 'cof_postcode',
  externalID: '',
  externalCustomerID: '',
}

type RefRecordWithValidateType = Partial<Record<keyof CheckoutCartV2Address, MutableRefObject<TextInputActionProps | null>>> & {
  validate(): boolean
}

function validateActionRefs(
  refRecord: RefRecordWithValidateType,
  keys: Array<keyof CheckoutCartV2Address>,
) {
  const validateActionRef: (ref: MutableRefObject<TextInputActionProps | null>) => boolean = (ref: MutableRefObject<TextInputActionProps | null>) => {
    if (!ref.current) {
      return true
    }
    ref.current?.touch()
    return !!ref.current?.validate()
  }
  return keys.reduce((acc: boolean[], key) => {
    const valid = validateActionRef(refRecord[key] || { current: null })
    acc.push(valid)
    if (!valid) {
      ampli.userInputError({ errors: `${analyticsIDRecord[key]}` })
    }
    return acc
  }, [])
    .every(a => !!a)
}

export interface CheckoutAddressStepProps {
}

export const CheckoutAddressStepV2: FC<PropsWithChildren<CheckoutAddressStepProps>> = ({ children }) => {
  const cofDesignV1 = useSplitTest('cof_design_v1', 'b')
  const { t, tDanger } = useTranslation()
  const actionRefs = useActionRefs()
  const {
    data,
    failedNavigation,
    failedNavigationAttempts,
    isRecording,
    model: {
      billingAddress,
      shippingAddress,
      showShippingAddress,
      subscribeToOffers,
    },
    setActiveStep,
    setShippingLoading,
    setSubscribeToClub,
    startRecording,
    stepValidations,
    subscribeToClub,
    updateStep,
    zipCodes,
  } = useContext(CheckoutFlowContext)
  const [saveAddressMutation, { loading }] = useSetAddressesInCartMutation()
  const [goNextIfValid, setGoNextIfValid] = useState(false)
  const { getValidations } = useCheckoutFieldsValidations()
  const { saveCheckoutLocal } = useSavedCheckoutLocalData()
  const billingValidations = useMemo(() => getValidations(billingAddress.country), [billingAddress.country, getValidations])
  const shippingValidations = useMemo(() => getValidations(shippingAddress.country), [getValidations, shippingAddress.country])
  const { subscribe } = useCheckoutSubscribeToMail()
  const phonePrefixItem = useCallback(option => <PhonePrefixItem prefix={PhonePrefixLocaleCodeMap[option] || undefined} title={option} />, [])
  const requiredText = useMemo(() => {
    const translation: string = t('checkout:COF_REQUIRED_FIELDS')
    return translation.startsWith('*') ? translation : `* ${translation}`
  }, [t])

  useEffect(() => {
    if (goNextIfValid && stepValidations.ADDRESS) {
      setActiveStep(CheckoutFlowSteps.SHIPPING)
      setGoNextIfValid(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [goNextIfValid, stepValidations])

  // SHOW ERRORS WHEN FAILING TO NAVIGATE BY RUNNING VALIDATIONS ON THE FIELDS
  useEffect(() => {
    if (failedNavigationAttempts && failedNavigationAttempts > 0 && failedNavigation === CheckoutFlowStep.ADDRESS) {
      // VALIDATE BILLING PART
      const billingInvalid = !validateActionRefs(
        actionRefs.billing,
        ['email', 'firstName', 'lastName', 'streetName', 'additionalStreetInfo', 'postalCode', 'city', 'country', 'phonePrefix', 'phone', 'state', 'region'],
      )
      if (billingInvalid) {
        document.getElementById('checkout-billing-address-section')
          ?.scrollIntoView({ behavior: 'smooth' })
        return
      }
      // VALIDATE SHIPPING PART
      if (showShippingAddress.checked) {
        const shippingInvalid = !validateActionRefs(
          actionRefs.shipping,
          ['firstName', 'lastName', 'company', 'streetName', 'additionalStreetInfo', 'postalCode', 'city', 'country', 'state', 'region'],
        )
        if (shippingInvalid && !billingInvalid) {
          document.getElementById('checkout-shipping-address-section')
            ?.scrollIntoView({ behavior: 'smooth' })
          return
        }
      }
      if (!goNextIfValid) {
        setGoNextIfValid(true)
      }
    }

    if (billingAddress.email
      && !data.cart.club?.isLoggedIn
      && (data.checkoutMeta.isOptInCountry && !!subscribeToOffers.checked)) {
      subscribe(billingAddress.email)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [failedNavigationAttempts])

  const saveAddressAbortController = useRef(typeof AbortController !== 'undefined' ? new AbortController() : null)
  const saveAddressMu = useMemo(() => debounce<typeof saveAddressMutation>((options) => {
    setShippingLoading(true)
    if (!saveAddressAbortController.current) {
      return saveAddressMutation({
        ...options,
        context: {
          domainProxy: true,
          userSpecific: true,
        },
      })
        .then((res) => {
          setShippingLoading(false)
          return res
        })
    }

    saveAddressAbortController.current.abort()
    saveAddressAbortController.current = new AbortController()

    return saveAddressMutation({
      ...options,
      context: {
        domainProxy: true,
        fetchOptions: {
          signal: saveAddressAbortController.current.signal,
        },
        userSpecific: true,
      },
    })
      .then((res) => {
        setShippingLoading(false)
        return res
      })
  }, 100), [saveAddressMutation, setShippingLoading])

  const saveAddress = useCallback(async (valid: boolean = true, code?: string) => {
    if (!valid) {
      ampli.userInputError({ errors: code })
    }
    if (!actionRefs.validate()) {
      ampli.userInputError({ errors: code })
      return undefined
    }
    const variables = {
      variables: {
        billingAddress: replaceObjectWhere(billingAddress, ['', null], undefined),
        shippingAddress: replaceObjectWhere(showShippingAddress.checked
          ? {
            ...shippingAddress,
            phone: billingAddress.phone,
            phonePrefix: billingAddress.phonePrefix,
          }
          : billingAddress, ['', null], undefined),
      },
    }
    let res: FetchResult<SetAddressesInCartMutation> | undefined
    try {
      res = await saveAddressMu(variables)
      if (!res?.data?.setAddressesInCart?.success || !isEmpty(res.errors)) {
        throw new Error('Failed saveAddressMutation 1st time')
      }
      return res
    } catch {
      try {
        res = await saveAddressMu(variables)
        if (!res?.data?.setAddressesInCart?.success || !isEmpty(res.errors)) {
          throw new Error('Failed saveAddressMutation 2nd time')
        }
        return res
      } catch {
        try {
          if (!isRecording && typeof window !== 'undefined' && 'hj' in window && isFunction(window.hj)) {
            startRecording()
            window.hj('trigger', 'onestepcheckout_address_error')
          }
          res = await saveAddressMu(variables)
          if (!res?.data?.setAddressesInCart?.success || !isEmpty(res.errors)) {
            throw new Error('Failed saveAddressMutation 3rd time')
          }
          return res
        } catch (err) {
          console.warn(err)
        }
      }
      return res
    }
  }, [actionRefs, billingAddress, isRecording, saveAddressMu, shippingAddress, showShippingAddress.checked, startRecording])

  useEffect(() => {
    if (billingAddress.email && validateActionRefs(
      actionRefs.billing,
      ['email'],
    )) {
      identify({ email: billingAddress.email })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const countries = data.checkoutMeta.addressMetaData?.countries || []
  const onlyOneCountry = countries.length === 1
  const states = (data.checkoutMeta.addressMetaData?.states || [])
  const manyStates = states.length > 1
  const regions = (data.checkoutMeta.addressMetaData?.regions || [])
  const manyRegions = regions.length > 1

  const clubTermsText = useMemo(() => {
    if (data.cart.club?.value) {
      return t('common:TRENDHIMCLUB_GET_CA_CASHBACK_FROM_THIS_ORDER', { amount: data.cart.club?.value.format })
    }

    return t('common:TRENDHIMCLUB_OVER_X_ALREADY_JOINED')
  }, [data.cart.club?.value, t])

  return (
    <div className={clsx(styles.checkoutAddressStep, { [styles.cofDesignV1]: cofDesignV1 })}>
      {children}
      {!data.cart.club?.isLoggedIn && (
        <ClubTerms accepted={subscribeToClub} className={styles.subscribeToClub} headerJoin onAccept={v => setSubscribeToClub(v)} v2>
          <p {...tDanger(clubTermsText)} />
        </ClubTerms>
      )}
      <CollapsableSection
        canCollapse={false}
        icon={!cofDesignV1 && <HomeIcon />}
        id="checkout-billing-address-section"
        isLoading={goNextIfValid && loading}
        title={!cofDesignV1 ? t('checkout:COF_ADDRESS') : undefined}
      >
        <h3 className={styles.yourInformation}>{cofDesignV1 ? t('checkout:CONTACT_INFORMATION') : t('checkout:COF_ENTER_YOUR_INFORMATION')}</h3>
        <form
          onKeyUp={(event: any) => {
            if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
              saveAddress(true).then((a) => {
                if (!a?.errors) {
                  setActiveStep(CheckoutFlowSteps.SHIPPING)
                }
              })
            }
          }}
        >
          <section>
            <TextInput
              actionRef={actionRefs.billing.email}
              autoComplete="billing email"
              className={styles.email}
              disabled={!!data.cart.club?.isLoggedIn}
              id="checkout-billing-email"
              label={`${t('checkout:COF_EMAIL_ADDRESS')} *`}
              onBlur={(e, v) => {
                if (v) {
                  identify({ email: e })
                }
              }}
              onChange={e => updateStep('billingAddress', { email: e })}
              validations={billingValidations.email}
              value={billingAddress.email || ''}
            />
            {!data.cart.club?.isLoggedIn && data.checkoutMeta.isOptInCountry && (
              <Checkbox
                className={styles.offers}
                label={t('checkout:CHECKOUT_EMAIL_OPT_IN')}
                setInputValue={(v) => {
                  updateStep('subscribeToOffers', { checked: v })
                  saveCheckoutLocal({ isOptInToNewsletter: v ? 'yes' : 'no' })
                }}
                value={subscribeToOffers.checked}
              />
            )}
            {!data.cart.club?.isLoggedIn && !data.checkoutMeta.isOptInCountry && (
              <Checkbox
                className={styles.offers}
                label={t('checkout:CHECKOUT_EMAIL_OPT_OUT')}
                setInputValue={(v) => {
                  updateStep('subscribeToOffers', { checked: !v })
                  saveCheckoutLocal({ isOptInToNewsletter: v ? 'no' : 'yes' })
                }}
                value={!subscribeToOffers.checked}
              />
            )}
            <TextInput
              actionRef={actionRefs.billing.firstName}
              autoComplete="billing given-name"
              className={styles.firstname}
              label={`${t('checkout:FIRST_NAME')} *`}
              onChange={e => updateStep('billingAddress', { firstName: e })}
              validations={billingValidations.firstName}
              value={billingAddress.firstName || ''}
            />
            <TextInput
              actionRef={actionRefs.billing.lastName}
              autoComplete="billing family-name"
              className={styles.lastname}
              label={`${t('checkout:LAST_NAME')} *`}
              onChange={e => updateStep('billingAddress', { lastName: e })}
              validations={billingValidations.lastName}
              value={billingAddress.lastName || ''}
            />
            {cofDesignV1 && (
              <h3 className={styles.yourInformation}>{t('checkout:ADDREESS_INFORMATION')}</h3>
            )}
            <TextInput
              actionRef={actionRefs.billing.streetName}
              autoComplete={`billing ${data.checkoutMeta.addressMetaData?.showAdditionalStreetInfo ? 'address-line1' : 'street-address'}`}
              className={styles.streetName}
              label={`${t('checkout:COF_ADDRESS_STREET_AND_NUMBER')} *`}
              onChange={e => updateStep('billingAddress', { streetName: e })}
              validations={billingValidations.streetName}
              value={billingAddress.streetName || ''}
            />
            {data.checkoutMeta.addressMetaData?.showAdditionalStreetInfo && (
              <TextInput
                actionRef={actionRefs.billing.additionalStreetInfo}
                autoComplete="billing address-line2"
                className={styles.additionalStreetInfo}
                label={`${t('checkout:COF_ADDRESS_LINE_2')}`}
                onChange={e => updateStep('billingAddress', { additionalStreetInfo: e })}
                validations={billingValidations.additionalStreetInfo}
                value={billingAddress.additionalStreetInfo || ''}
              />
            )}
            <TextInput
              actionRef={actionRefs.billing.postalCode}
              autoComplete="billing postal-code"
              className={styles.postalCode}
              label={`${t('checkout:COF_POSTCODE')} *`}
              onChange={(e) => {
                const postal = addZipCodeDash(e.trimStart()
                  .replaceAll('  ', ' '), billingAddress.country || '')
                if (zipCodes[e]) {
                  updateStep('billingAddress', {
                    postalCode: postal,
                    city: zipCodes[e],
                  })
                  actionRefs.billing.city.current?.validate()
                  return
                }
                updateStep('billingAddress', { postalCode: postal })
              }}
              validations={billingValidations.postalCode}
              value={billingAddress.postalCode || ''}
            />
            <TextInput
              actionRef={actionRefs.billing.city}
              autoComplete="billing address-level2"
              className={styles.city}
              label={`${t('checkout:COF_CITY')} *`}
              onChange={e => updateStep('billingAddress', { city: e })}
              validations={billingValidations.city}
              value={billingAddress.city || ''}
            />
            <SelectObject
              actionRef={actionRefs.billing.country}
              className={clsx(
                styles.country,
                !(data.checkoutMeta.addressMetaData?.showState || data.checkoutMeta.addressMetaData?.showRegion) && styles.fill,
              )}
              disabled={onlyOneCountry}
              getObjectLabel={value => value?.name}
              label={`${t('checkout:COF_COUNTRY')} *`}
              onChange={(country, v) => {
                updateStep('billingAddress', {
                  country: country.code,
                  phonePrefix: PhonePrefixCountryCodeMap[country.code].phonePrefix,
                })
                if (!isEmpty(billingAddress.postalCode)) {
                  setTimeout(() => {
                    actionRefs?.billing?.postalCode?.current?.touch()
                    actionRefs?.billing?.postalCode?.current?.validate()
                    saveAddress(v, 'cof_country')
                  }, 1)
                }
              }}
              options={countries}
              validations={billingValidations.country}
              value={countries.find(country => country.code === billingAddress.country) || first(countries)}
            />
            {data.checkoutMeta.addressMetaData?.showState && (
              manyStates
                ? (
                  <SelectInput
                    actionRef={actionRefs.billing.state}
                    autoComplete="billing address-level3"
                    className={styles.state}
                    deselect={billingAddress.state ? undefined : ''}
                    label={`${t('checkout:COF_SELECT_A_REGION')} *`}
                    onChange={(e, v) => {
                      updateStep('billingAddress', { state: e })
                      saveAddress(v, 'cof_country')
                    }}
                    options={states}
                    validations={billingValidations.state}
                    value={billingAddress.state || ''}
                  />
                )
                : (
                  <TextInput
                    actionRef={actionRefs.billing.state}
                    className={styles.state}
                    label={`${t('checkout:COF_REGION')} *`}
                    onChange={e => updateStep('billingAddress', { state: e })}
                    validations={billingValidations.state}
                    value={billingAddress.state || ''}
                  />
                )
            )}
            {data.checkoutMeta.addressMetaData?.showRegion && (
              manyRegions
                ? (
                  <SelectInput
                    actionRef={actionRefs.billing.region}
                    autoComplete="billing address-level3"
                    className={styles.region}
                    deselect={billingAddress.region ? undefined : ''}
                    label={`${t('checkout:COF_SELECT_A_REGION')} *`}
                    onChange={(e, v) => {
                      updateStep('billingAddress', { region: e })
                      saveAddress(v, 'cof_country')
                    }}
                    options={regions}
                    validations={billingValidations.region}
                    value={billingAddress.region || ''}
                  />
                )
                : (
                  <TextInput
                    actionRef={actionRefs.billing.region}
                    className={styles.region}
                    label={`${t('checkout:COF_REGION')} *`}
                    onChange={e => updateStep('billingAddress', { region: e })}
                    validations={billingValidations.region}
                    value={billingAddress.region || ''}
                  />
                )
            )}
            <SelectInputCustom
              actionRef={actionRefs.billing.phonePrefix}
              ariaLabel={t('checkout:COF_PHONE_PREFIX')}
              className={styles.phonePrefix}
              customElement={phonePrefixItem}
              onChange={(e, v) => {
                updateStep('billingAddress', { phonePrefix: e })
                saveAddress(v, 'cof_phone')
              }}
              options={Object.keys(PhonePrefixLocaleCodeMap)}
              showValid={false}
              validations={billingValidations.phonePrefix}
              value={billingAddress.phonePrefix || (billingAddress.country ? PhonePrefixCountryCodeMap[billingAddress.country]?.phonePrefix : '')}
            />
            <TextInput
              actionRef={actionRefs.billing.phone}
              autoComplete="tel-national"
              className={styles.phone}
              label={`${t('checkout:COF_TELEPHONE_NUMBER')} *`}
              onChange={(e) => {
                updateStep('billingAddress', { phone: e })
              }}
              type="number"
              validations={billingValidations.phone}
              value={billingAddress.phone || ''}
            />
          </section>
        </form>
        <p className={styles.tracking}>{t('checkout:ONLY_USED_TO_SEND_TACKING_INFORMATION')}</p>
        <Checkbox
          className={styles.showShipping}
          label={t('checkout:SHIP_TO_DIFFERENT_ADDRESS')}
          setInputValue={(e) => {
            updateStep('showShippingAddress', { checked: e })
            saveAddress(true, '')
          }}
          value={showShippingAddress.checked}
        />
        {showShippingAddress.checked && (
          <form
            onKeyUp={(event: any) => {
              if (event.key === 'Enter' && event.target instanceof HTMLInputElement) {
                saveAddress(true)
                  .then((a) => {
                    if (!a?.errors) {
                      setActiveStep(CheckoutFlowSteps.SHIPPING)
                    }
                  })
              }
            }}
          >
            <section>
              <h3 className={styles.deliveryTitle} id="checkout-shipping-address-section">{t('checkout:COF_DELIVERY_ADDRESS')}</h3>

              <TextInput
                actionRef={actionRefs.shipping.firstName}
                autoComplete="shipping given-name"
                className={styles.firstname}
                label={`${t('checkout:FIRST_NAME')} *`}
                onChange={e => updateStep('shippingAddress', { firstName: e })}
                validations={shippingValidations.firstName}
                value={shippingAddress.firstName || ''}
              />
              <TextInput
                actionRef={actionRefs.shipping.lastName}
                autoComplete="shipping family-name"
                className={styles.lastname}
                label={`${t('checkout:LAST_NAME')} *`}
                onChange={e => updateStep('shippingAddress', { lastName: e })}
                validations={shippingValidations.lastName}
                value={shippingAddress.lastName || ''}
              />
              <TextInput
                actionRef={actionRefs.shipping.company}
                autoComplete="shipping organization"
                className={styles.company}
                label={`${t('checkout:COF_COMPANY')}`}
                onChange={e => updateStep('shippingAddress', { company: e })}
                validations={shippingValidations.company}
                value={shippingAddress.company || ''}
              />

              <TextInput
                actionRef={actionRefs.shipping.streetName}
                autoComplete={`shipping ${data.checkoutMeta.addressMetaData?.showAdditionalStreetInfo ? 'address-line1' : 'street-address'}`}
                className={styles.streetName}
                label={`${t('checkout:COF_ADDRESS_STREET_AND_NUMBER')} *`}
                onChange={e => updateStep('shippingAddress', { streetName: e })}
                validations={shippingValidations.streetName}
                value={shippingAddress.streetName || ''}
              />
              {data.checkoutMeta.addressMetaData?.showAdditionalStreetInfo && (
                <TextInput
                  actionRef={actionRefs.shipping.additionalStreetInfo}
                  autoComplete="shipping address-line2"
                  className={styles.additionalStreetInfo}
                  label={`${t('checkout:COF_ADDRESS_LINE_2')}`}
                  onChange={e => updateStep('shippingAddress', { additionalStreetInfo: e })}
                  validations={shippingValidations.additionalStreetInfo}
                  value={shippingAddress.additionalStreetInfo || ''}
                />
              )}
              <TextInput
                actionRef={actionRefs.shipping.postalCode}
                autoComplete="shipping postal-code"
                className={styles.postalCode}
                label={`${t('checkout:COF_POSTCODE')} *`}
                onChange={(e) => {
                  const postal = addZipCodeDash(e.trimStart()
                    .replaceAll('  ', ' '), shippingAddress.country || '')
                  if (zipCodes[e]) {
                    updateStep('shippingAddress', {
                      postalCode: postal,
                      city: zipCodes[e],
                    })
                    actionRefs.shipping.city.current?.validate()
                    return
                  }
                  updateStep('shippingAddress', { postalCode: postal })
                }}
                validations={shippingValidations.postalCode}
                value={shippingAddress.postalCode || ''}
              />
              <TextInput
                actionRef={actionRefs.shipping.city}
                autoComplete="shipping address-level2"
                className={styles.city}
                label={`${t('checkout:COF_CITY')} *`}
                onChange={e => updateStep('shippingAddress', { city: e })}
                validations={shippingValidations.city}
                value={shippingAddress.city || ''}
              />
              <SelectObject
                actionRef={actionRefs.shipping.country}
                className={clsx(
                  styles.country,
                  !(data.checkoutMeta.addressMetaData?.showState || data.checkoutMeta.addressMetaData?.showRegion) && styles.fill,
                )}
                disabled={onlyOneCountry}
                getObjectLabel={value => value?.name}
                label={`${t('checkout:COF_COUNTRY')} *`}
                onChange={(country, v) => {
                  updateStep('shippingAddress', { country: country.code })
                  if (!isEmpty(shippingAddress.postalCode)) {
                    setTimeout(() => {
                      actionRefs?.shipping?.postalCode?.current?.touch()
                      actionRefs?.shipping?.postalCode?.current?.validate()
                      saveAddress(v, 'cof_country')
                    }, 1)
                  }
                }}
                options={countries}
                validations={billingValidations.country}
                value={countries.find(country => country.code === shippingAddress.country) || first(countries)}
              />
              {data.checkoutMeta.addressMetaData?.showState && (
                manyStates
                  ? (
                    <SelectInput
                      actionRef={actionRefs.shipping.state}
                      autoComplete="shipping address-level3"
                      className={styles.state}
                      deselect={shippingAddress.state ? undefined : ''}
                      label={`${t('checkout:COF_SELECT_A_REGION')} *`}
                      onChange={(e, v) => {
                        updateStep('shippingAddress', { state: e })
                        saveAddress(v, 'cof_country')
                      }}
                      options={states}
                      validations={shippingValidations.state}
                      value={shippingAddress.state || ''}
                    />
                  )
                  : (
                    <TextInput
                      actionRef={actionRefs.shipping.state}
                      className={styles.state}
                      label={`${t('checkout:COF_REGION')} *`}
                      onChange={e => updateStep('shippingAddress', { state: e })}
                      validations={shippingValidations.state}
                      value={shippingAddress.state || ''}
                    />
                  )
              )}
              {data.checkoutMeta.addressMetaData?.showRegion && (
                manyRegions
                  ? (
                    <SelectInput
                      actionRef={actionRefs.shipping.region}
                      autoComplete="shipping address-level3"
                      className={styles.region}
                      deselect={shippingAddress.region ? undefined : ''}
                      label={`${t('checkout:COF_SELECT_A_REGION')} *`}
                      onChange={(e, v) => {
                        updateStep('shippingAddress', { region: e })
                        saveAddress(v, 'cof_country')
                      }}
                      options={regions}
                      validations={shippingValidations.region}
                      value={shippingAddress.region || ''}
                    />
                  )
                  : (
                    <TextInput
                      actionRef={actionRefs.shipping.region}
                      className={styles.region}
                      label={`${t('checkout:COF_REGION')} *`}
                      onChange={e => updateStep('shippingAddress', { region: e })}
                      validations={shippingValidations.region}
                      value={shippingAddress.region || ''}
                    />
                  )
              )}
            </section>
          </form>
        )}
        <p className={styles.requiredFields}>{requiredText}</p>

      </CollapsableSection>
      <span className={styles.buttonWrapper}>
        {cofDesignV1 && (
          <Button
            className={styles.continueShopping}
            href="/"
            id="checkout-address-next-button"
            innerSpacing="medium"
            theme="label"
          >
            <div className={styles.continueShoppingIcon}>
              <SvgAngleLeft />
            </div>
            {t('cart:PP_CONTINUE_SHOPPING_BUTTON_TEXT')}
          </Button>
        )}
        <Button
          className={clsx(!cofDesignV1 && styles.newDesign)}
          disabled={loading}
          id="checkout-address-next-button"
          innerSpacing="medium"
          onClick={() => {
            saveAddress(true).then((a) => {
              if (!a?.errors) {
                setActiveStep(CheckoutFlowSteps.SHIPPING)
              }
            })
          }}
          theme="black"
        >
          {t('checkout:NEXT_LABEL')}
        </Button>
      </span>
    </div>
  )
}

